import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import { GitBookUsers } from '@gitbook/api-client';
import { getUserDisplayName } from '@gitbook/app-database';
import { colors } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Avatar, type AvatarProps, type AvatarSize, resolveAvatarSize } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { getImageResizedURL } from '../../utils';
import { getAnonymousColors, getRealTimeColor } from './utils';

export type UserObject = ({ uid: string } | { id: string } | { objectID: string }) & {
    displayName?: string | null;
    photoURL?: string | null;
};

type AvatarData = {
    userId: string;
    displayName: string;
    photoURL: string | null;
};

export type UserId = string;

export type UserAvatarProps = Pick<
    AvatarProps,
    'tooltip' | 'tooltipLabel' | 'tooltipPrefix' | 'size' | 'className' | 'squared'
> & {
    /**
     * The user to display, can be a userId or a user object.
     */
    user: UserObject | UserId | null | undefined;

    /** Indicate that this user is active */
    realtime?: boolean;

    /** If the avatar is in a stack, add a theme-dependent border */
    inStack?: boolean;
};

const ANONYMOUS_DATA: AvatarData = {
    userId: 'anonymous',
    displayName: 'Anonymous',
    photoURL: null,
};

/**
 * Avatar for an user.
 */
export const UserAvatar = React.forwardRef(function UserAvatarWithRef(
    props: UserAvatarProps,
    ref: React.Ref<HTMLDivElement>
) {
    const avatar = extractAvatarDataFromUserProp(props.user);

    if (
        props.user === GitBookUsers.GitbookAgent ||
        (typeof props.user === 'object' &&
            props.user &&
            'id' in props.user &&
            props.user.id === GitBookUsers.GitbookAgent)
    ) {
        return <GitBookAgentAvatar ref={ref} {...props} />;
    }

    if (avatar) {
        return <UserAvatarWithData ref={ref} {...props} avatar={avatar} />;
    }

    const userId = extractUserFromUserProp(props.user);

    if (userId) {
        return (
            <React.Suspense fallback={<UserAvatarSkeleton ref={ref} {...props} />}>
                <UserAvatarFetcher ref={ref} userId={userId} {...props} />
            </React.Suspense>
        );
    }

    return <UserAvatarWithData ref={ref} avatar={ANONYMOUS_DATA} {...props} />;
});

/**
 * Avatar for the GitBook Agent.
 */
export const GitBookAgentAvatar = React.forwardRef(function GitBookAgentAvatar(
    props: Pick<UserAvatarProps, 'squared' | 'realtime' | 'inStack'> & {
        size?: AvatarSize | number;
        loading?: boolean;
    },
    ref: React.Ref<HTMLDivElement>
) {
    const { loading = false, realtime, inStack, squared } = props;
    const size = resolveAvatarSize(props.size);
    return (
        <div
            ref={ref}
            className={clsx(
                'flex items-center justify-center bg-muted text-muted',
                size.dimensions,
                realtime || inStack
                    ? 'box-content border-2 border-[--sp-color-border-avatar]'
                    : null,
                squared ? 'rounded' : 'rounded-full'
            )}
        >
            <Icon.AIAgentAnimated
                state={loading ? 'loading' : 'default'}
                className="block size-[1.4em]"
            />
        </div>
    );
});

export const UserAvatarSkeleton = React.forwardRef(function UserAvatarSkeleton(
    props: Pick<UserAvatarProps, 'squared' | 'realtime' | 'inStack'> & {
        size?: AvatarSize | number;
    },
    ref: React.Ref<HTMLDivElement>
) {
    const { realtime, squared, inStack } = props;
    const size = resolveAvatarSize(props.size);
    return (
        <div
            ref={ref}
            aria-busy
            className={clsx(
                'shimmer',
                size.dimensions,
                realtime || inStack ? 'box-content border-[--sp-color-border-avatar]' : null,
                squared ? 'rounded' : 'rounded-full'
            )}
            style={realtime || inStack ? { borderWidth: size.borderWidth } : undefined}
        />
    );
});

const UserAvatarFetcher = React.forwardRef(function UserAvatarFetcher(
    props: Omit<UserAvatarProps, 'user'> & { userId: string; size?: AvatarSize | number },
    ref: React.Ref<HTMLDivElement>
) {
    const { data } = useSuspenseQuery(
        apiQuery('getUserById', { userId: props.userId }, { nullOnError: true })
    );

    const avatar: AvatarData = React.useMemo(() => {
        if (!data) {
            return { userId: props.userId, displayName: 'Anonymous', photoURL: null };
        }
        return {
            userId: props.userId,
            displayName: data.displayName,
            photoURL: data.photoURL ?? null,
        };
    }, [data, props.userId]);

    return <UserAvatarWithData ref={ref} {...props} avatar={avatar} />;
});

/**
 * Extract user data from the user prop.
 * Returns null if data are not available.
 */
function extractAvatarDataFromUserProp(userProp: UserAvatarProps['user']): AvatarData | null {
    if (!userProp) {
        return null;
    }

    if (typeof userProp === 'string') {
        return null;
    }

    // If the userObject is not complete, then we will fetch the user data.
    if (!('photoURL' in userProp) || !('displayName' in userProp)) {
        return null;
    }

    const id = extractUserFromUserProp(userProp);

    if (!id) {
        return null;
    }

    return {
        userId: id,
        displayName: getUserDisplayName(userProp),
        photoURL: userProp.photoURL ?? null,
    };
}

/**
 * Extract a user id when the api is both user.uid or user.id.
 */
function extractUserFromUserProp(userProp: UserAvatarProps['user']): string | null {
    if (!userProp) {
        return null;
    }

    if (typeof userProp === 'string') {
        return userProp;
    }

    if ('uid' in userProp && userProp.uid) {
        return userProp.uid;
    }

    if ('id' in userProp && userProp.id) {
        return userProp.id;
    }

    if ('objectID' in userProp && userProp.objectID) {
        return userProp.objectID;
    }

    return null;
}

const UserAvatarWithData = React.forwardRef(function UserAvatarWithDataRef(
    props: Omit<UserAvatarProps, 'user'> & { avatar: AvatarData },
    ref: React.Ref<HTMLDivElement>
) {
    const { avatar, realtime = false, inStack } = props;

    const colors = getRealTimeColor(avatar.userId);
    const fallbackColors = getAnonymousColors(avatar.userId);

    return (
        <Avatar
            ref={ref}
            displayName={avatar.displayName}
            photoURL={avatar.photoURL && getImageResizedURL(avatar.photoURL)}
            fallbackColors={fallbackColors}
            borderColor={
                // Passing a spine token as colors.base can not be a token, and preferable to keep the border styling in a single prop.
                realtime ? colors.base : inStack ? 'var(--sp-color-border-avatar)' : undefined
            }
            {...props}
        />
    );
});

/**
 * Returns the color associated to a user based on its displayName.
 */
export function getUserColor(displayName: string): string {
    const colorOptions = [
        colors.primary.base,
        colors.green.base,
        colors.violet.base,
        colors.fuchsia.base,
        colors.orange.base,
    ];
    const alpha = displayName.toLowerCase().charCodeAt(0) - 97;
    const colorPosition = alpha % colorOptions.length;
    return colorOptions[colorPosition];
}
